// client/src/utils/whatsappUtils.js
import React from 'react';


/**
 * Detecta se o usuário está em um dispositivo móvel
 * @returns {boolean} true se for mobile, false se for desktop
 */
export const isMobileDevice = () => {
  // Múltiplas verificações para maior precisão
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Verifica por palavras-chave no user agent
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase());
  
  // Verifica também pelo tamanho da tela (tablets grandes podem ser considerados desktop)
  const isMobileScreen = window.innerWidth <= 768;
  
  // Verifica se tem capacidade de toque
  const hasTouchScreen = 'ontouchstart' in window || 
                         navigator.maxTouchPoints > 0 || 
                         navigator.msMaxTouchPoints > 0;
  
  // Considera mobile se: 
  // - O user agent indica mobile OU
  // - A tela é pequena E tem touch
  return isMobileUA || (isMobileScreen && hasTouchScreen);
};

/**
 * Gera a URL correta do WhatsApp baseada no dispositivo
 * @param {string} phoneNumber - Número do telefone (com código do país, sem +)
 * @param {string} message - Mensagem pré-formatada
 * @returns {string} URL completa do WhatsApp
 */
export const generateWhatsAppUrl = (phoneNumber, message) => {
  // Remove qualquer formatação do número
  const cleanNumber = phoneNumber.replace(/\D/g, '');
  
  // Codifica a mensagem para URL
  const encodedMessage = encodeURIComponent(message);
  
  // Decide qual domínio usar baseado no dispositivo
  const domain = isMobileDevice() 
    ? 'api.whatsapp.com/send' 
    : 'web.whatsapp.com/send';
  
  // Monta a URL completa
  return `https://${domain}?phone=${cleanNumber}&text=${encodedMessage}`;
};

/**
 * Converte URLs existentes do WhatsApp para o formato correto
 * @param {string} url - URL original do WhatsApp
 * @returns {string} URL ajustada para o dispositivo
 */
export const convertWhatsAppUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  
  // Verifica se é uma URL do WhatsApp
  if (!url.includes('whatsapp.com')) return url;
  
  // Extrai os parâmetros da URL original
  try {
    const urlObj = new URL(url);
    const phone = urlObj.searchParams.get('phone');
    const text = urlObj.searchParams.get('text');
    
    if (phone) {
      // Decodifica o texto para recodificar corretamente
      const decodedText = text ? decodeURIComponent(text) : '';
      return generateWhatsAppUrl(phone, decodedText);
    }
  } catch (error) {
    console.error('Erro ao converter URL do WhatsApp:', error);
  }
  
  // Se não conseguir processar, retorna a original
  return url;
};

/**
 * Hook React para usar URLs do WhatsApp (opcional)
 * @param {string} phoneNumber 
 * @param {string} message 
 * @returns {string} URL do WhatsApp apropriada
 */
export const useWhatsAppUrl = (phoneNumber, message) => {
  const [url, setUrl] = React.useState('');
  
  React.useEffect(() => {
    // Atualiza a URL quando mudar o tamanho da janela (responsivo)
    const updateUrl = () => {
      setUrl(generateWhatsAppUrl(phoneNumber, message));
    };
    
    updateUrl();
    
    // Adiciona listener para mudanças no tamanho da janela
    window.addEventListener('resize', updateUrl);
    
    return () => window.removeEventListener('resize', updateUrl);
  }, [phoneNumber, message]);
  
  return url;
};

// Exporta também uma versão que pode ser usada diretamente em onclick
export const openWhatsApp = (phoneNumber, message) => {
  const url = generateWhatsAppUrl(phoneNumber, message);
  window.open(url, '_blank');
};

// Função helper para extrair número e mensagem de uma URL existente
export const parseWhatsAppUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return {
      phone: urlObj.searchParams.get('phone') || '',
      text: decodeURIComponent(urlObj.searchParams.get('text') || '')
    };
  } catch {
    return { phone: '', text: '' };
  }
};